import {
	Component, Input, Output, OnInit, OnDestroy, OnChanges, SimpleChanges, SimpleChange,
	EventEmitter, ChangeDetectorRef, ViewChild
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { IdentityService } from '../../services/identity.service';
import { UsersService, UsersServiceEvent, UsersServiceEventType } from '../../services/users.service';
import { MessageService } from 'base';
import { List } from 'DiscoveryEngine.TS/Utils';
import {
	UserDto, UserClass, AccountType, LoginResultDto, LoginResultCode,
	ActiveDirectorySettingsDto, GoogleWorkspaceSettingsDto, getAccountTypeLabel, DirectoryUserDto
} from 'UvExplorerServer.Dtos.TS/Auth';
import { LicensingService } from 'identity';
import { ObjSerUtils } from 'DiscoveryEngine.TS/Serialization';
import { MenuItem } from 'primeng/api';
import { SamlSettingsDto } from 'UvExplorerServer.Dtos.TS/Auth';


enum DelayedOperationType {
	None,
	CreateActiveDirectoryUser,
	CreateGoogleWorkspaceUser,
}


@Component({
    selector: 'user-list',
	templateUrl: './user-list.component.html',
	styleUrls: ['./user-list.component.scss']
})
export class UserListComponent implements OnInit, OnDestroy, OnChanges {

 	public entries: any[];
	private userToDelete: UserDto;
	public confirmDeleteDialogVisible: boolean;

	public userDialogVisible: boolean;
	public userDialogTarget: UserDto;
	public userDialogCreate: boolean;

	public adAdminDialogVisible: boolean;
	public adAdminDialogDomains: string[];

	public gwAdminDialogVisible: boolean;

	public dirUserDialogType: AccountType;
	public dirUserDialogVisible: boolean;
	public dirUserDialogTarget: UserDto;
	public dirUserDialogCreate: boolean;
	public dirUserDialogDomains: string[];
	public dirUserDialogUsers: DirectoryUserDto[];

	private contextUserGuid: string;

	private adSettingsLoadedSub: Subscription;
	private gwSettingsLoadedSub: Subscription;
	private samlSettingsLoadedSub: Subscription;
	private adSettings: ActiveDirectorySettingsDto;
	private gwSettings: GoogleWorkspaceSettingsDto;
	private samlSettings: SamlSettingsDto;

	private onUserChangeSub: Subscription;

	public createMenuItems: MenuItem[];

	// When re-entering the app during OAuth flow, createDirectoryUser was being called
	// before the directory settings had been asyncronously loaded.
	// Therefore, it was necessary to add support for delayed operations that are run
	// once the directory settings have been loaded.
	private delayedOpType: DelayedOperationType;

	constructor(private identityService: IdentityService,
		private usersService: UsersService,
		private licensingService: LicensingService,
		private messageService: MessageService) {

		this.entries = [];
		this.userToDelete = null;
		this.confirmDeleteDialogVisible = false;

		this.resetUserDialogState();
		this.resetAdAdminDialogState();
		this.resetGwAdminDialogState();
		this.resetDirUserDialogState();
		this.resetContextState();

		this.adSettings = null;
		this.gwSettings = null;
		this.samlSettings = null;

		this.delayedOpType = DelayedOperationType.None;

		this.adSettingsLoadedSub = this.usersService.onActiveDirectorySettingsLoaded.subscribe(
			(result: ActiveDirectorySettingsDto) => {
				this.copyAdSettings();
				this.initMenuItems();

				if (this.delayedOpType === DelayedOperationType.CreateActiveDirectoryUser) {
					this.delayedOpType = DelayedOperationType.None;
					this.createDirectoryUser(AccountType.ActiveDirectory);
				}
			});
		this.gwSettingsLoadedSub = this.usersService.onGoogleWorkspaceSettingsLoaded.subscribe(
			(result: GoogleWorkspaceSettingsDto) => {
				this.copyGwSettings();
				this.initMenuItems();

				if (this.delayedOpType === DelayedOperationType.CreateGoogleWorkspaceUser) {
					this.delayedOpType = DelayedOperationType.None;
					this.createDirectoryUser(AccountType.GoogleWorkspace);
				}
			});
		this.samlSettingsLoadedSub = this.usersService.onSamlSettingsLoaded.subscribe(
			(result: SamlSettingsDto) => {
				this.copySamlSettings();
				this.initMenuItems();
			});

		this.onUserChangeSub = this.usersService.onUserChange.subscribe(
			(event: UsersServiceEvent) => {
				if (event.type === UsersServiceEventType.UserCreate) {
					event.users.forEach((user: UserDto) => this.addEntry(user));
				}
			});
	}

	private initMenuItems(): void {
		this.initCreateMenuItems();
	}

	private initCreateMenuItems(): void {
		this.createMenuItems = [
			{
				icon: "fa fa-plus-square-o",
				label: "UVexplorer User",
				command: () => this.createUser(AccountType.Native),
				disabled: !this.isLicenseValid,
			}
		];

		if (!!this.adSettings && this.adSettings.Enabled) {
			this.createMenuItems.push(
				{
					icon: "fa fa-plus-square-o",
					label: "Active Directory User",
					command: () => this.createActiveDirectoryUser(),
					disabled: !this.isLicenseValid,
				}
			);
		}

		if (!!this.gwSettings && this.gwSettings.Enabled) {
			this.createMenuItems.push(
				{
					icon: "fa fa-plus-square-o",
					label: "Google Workspace User",
					command: () => this.createGoogleWorkspaceUser(),
					disabled: !this.isLicenseValid,
				}
			);
		}

		if (!!this.samlSettings && this.samlSettings.Enabled) {
			this.createMenuItems.push(
				{
					icon: "fa fa-plus-square-o",
					label: "SAML User",
					command: () => this.createUser(AccountType.Saml),
					disabled: !this.isLicenseValid,
				}
			);
		}
	}

	private resetUserDialogState(): void {
		this.userDialogVisible = false;
		this.userDialogTarget = null;
		this.userDialogCreate = false;
	}

	private resetAdAdminDialogState(): void {
		this.adAdminDialogVisible = false;
		this.adAdminDialogDomains = [];
	}

	private resetGwAdminDialogState(): void {
		this.gwAdminDialogVisible = false;
	}

	private resetDirUserDialogState(): void {
		this.dirUserDialogType = AccountType.Native;
		this.dirUserDialogVisible = false;
		this.dirUserDialogTarget = null;
		this.dirUserDialogCreate = false;
		this.dirUserDialogDomains = [];
		this.dirUserDialogUsers = [];
	}

	private resetContextState(): void {
		this.contextUserGuid = "";
	}

	public get hasEntries(): boolean {
		return (this.entries.length > 0);
	}

	private copyAdSettings(): void {
		if (this.usersService.hasActiveDirectorySettings) {
			this.adSettings = new ActiveDirectorySettingsDto();
			ObjSerUtils.Copy(this.usersService.adSettings, this.adSettings);
		}
		else {
			this.adSettings = null;
		}
	}

	private copyGwSettings(): void {
		if (this.usersService.hasGoogleWorkspaceSettings) {
			this.gwSettings = new GoogleWorkspaceSettingsDto();
			ObjSerUtils.Copy(this.usersService.gwSettings, this.gwSettings);
		}
		else {
			this.gwSettings = null;
		}
	}

	private copySamlSettings(): void {
		if (this.usersService.hasSamlSettings) {
			this.samlSettings = new SamlSettingsDto();
			ObjSerUtils.Copy(this.usersService.samlSettings, this.samlSettings);
		}
		else {
			this.samlSettings = null;
		}
	}

	public ngOnInit(): void {
		this.refreshUsers();

		if (this.usersService.hasActiveDirectorySettings) {
			this.copyAdSettings();
		}
		if (this.usersService.hasGoogleWorkspaceSettings) {
			this.copyGwSettings();
		}
		if (this.usersService.hasSamlSettings) {
			this.copySamlSettings();
		}

		this.initMenuItems();
	}

	public ngOnDestroy(): void {
		if (this.adSettingsLoadedSub) {
			this.adSettingsLoadedSub.unsubscribe();
			this.adSettingsLoadedSub = null;
		}
		if (this.gwSettingsLoadedSub) {
			this.gwSettingsLoadedSub.unsubscribe();
			this.gwSettingsLoadedSub = null;
		}
		if (this.samlSettingsLoadedSub) {
			this.samlSettingsLoadedSub.unsubscribe();
			this.samlSettingsLoadedSub = null;
		}
		if (this.onUserChangeSub) {
			this.onUserChangeSub.unsubscribe();
			this.onUserChangeSub = null;
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {
		
	}

	public get isLicenseValid(): boolean {
		//return this.licensingService.isLicenseValid;
		return true;
	}

	public get isActiveDirectoryEnabled(): boolean {
		return (!!this.adSettings && this.adSettings.Enabled);
	}

	public get isGoogleWorkspaceEnabled(): boolean {
		return (!!this.gwSettings && this.gwSettings.Enabled);
	}

	public refreshUsers(): void {
		if (this.identityService.isLoggedIn) {
			this.getAllUsers();
		}
	}

	public createUser(accountType: AccountType): void {
		this.userDialogCreate = true;
		this.userDialogTarget = new UserDto();
		this.userDialogTarget.AccountType = accountType;
		this.userDialogVisible = true;
	}

	public createActiveDirectoryUser(): void {

		this.identityService.testAdmin(AccountType.ActiveDirectory).subscribe(
			(result: LoginResultDto) => {
				if (result.Code === LoginResultCode.Succeeded) {
					this.createDirectoryUser(AccountType.ActiveDirectory);
				}
				else {
					this.dirUserDialogCreate = true;

					this.adAdminDialogDomains = this.usersService.adSettings.DomainNames.ToArray();
					this.adAdminDialogVisible = true;
				}
			},
			(error) => {
				this.messageService.displayErrorMessage("Could not verify Active Directory admin credentials");
			}
		);
	}

	public onAdAdminDialogOkClicked({ username: username,
		password: password, domain: domain }): void {

		this.identityService.loginActiveDirectoryAdmin(
			username, password, domain).subscribe(
				(result: LoginResultDto) => {
					if (result.Code === LoginResultCode.Succeeded) {
						if (this.dirUserDialogCreate) {
							this.createDirectoryUser(AccountType.ActiveDirectory);
						}
						else {
							this.editDirectoryUser(this.dirUserDialogTarget);
						}
					}
					else {
						this.resetDirUserDialogState();
						this.messageService.displayErrorMessage("Login failed");
					}
				},
				(error) => {
					this.resetDirUserDialogState();
					this.messageService.displayErrorMessage("Login failed");
				}
		);

		this.resetAdAdminDialogState();
	}

	public onAdAdminDialogCancelClicked(): void {
		this.resetAdAdminDialogState();
		this.resetDirUserDialogState();
	}

	public createGoogleWorkspaceUser(): void {

		this.identityService.testAdmin(AccountType.GoogleWorkspace).subscribe(
			(result: LoginResultDto) => {
				if (result.Code === LoginResultCode.Succeeded) {
					this.createDirectoryUser(AccountType.GoogleWorkspace);
				}
				else {
					this.dirUserDialogCreate = true;

					this.gwAdminDialogVisible = true;
				}
			},
			(error) => {
				this.messageService.displayErrorMessage("Could not verify Google Workspace admin credentials");
			}
		);
	}

	public onGwAdminDialogOkClicked(): void {

		let finalRedirectUrl = "";
		let errorRedirectUrl = "/admin/userlist/loginadmin/failed";

		if (this.dirUserDialogCreate) {
			finalRedirectUrl = location.origin + "/admin/userlist/createuser/googleworkspace";
		}
		else {
			finalRedirectUrl = location.origin + "/admin/userlist/edituser/googleworkspace/" + this.dirUserDialogTarget.Guid;
		}

		this.identityService.loginGoogleWorkspaceAdmin(finalRedirectUrl, errorRedirectUrl).subscribe(
			(result: LoginResultDto) => {
				// Do nothing because a successful call will result in a redirect
			},
			(error) => {
				this.messageService.displayErrorMessage("Could not initiate Google admin login");
			}
		);

		this.resetGwAdminDialogState();
	}

	public onGwAdminDialogCancelClicked(): void {
		this.resetGwAdminDialogState();
		this.resetDirUserDialogState();
	}

	public createDirectoryUser(userType: AccountType): void {

		// When re-entering the app during OAuth flow, createDirectoryUser was being called
		// before the directory settings had been asyncronously loaded.
		// Therefore, it was necessary to add support for delayed operations that are run
		// once the directory settings have been loaded.

		switch (userType) {
			case AccountType.ActiveDirectory:
				if (!this.adSettings) {
					this.delayedOpType = DelayedOperationType.CreateActiveDirectoryUser;
					return;
				}
				break;
			case AccountType.GoogleWorkspace:
				if (!this.gwSettings) {
					this.delayedOpType = DelayedOperationType.CreateGoogleWorkspaceUser;
					return;
				}
				break;
			default:
				console.assert(false);
				console.error("Unknown account type");
				return;
		}

		this.dirUserDialogType = userType;
		this.dirUserDialogCreate = true;
		this.dirUserDialogTarget = new UserDto();
		this.dirUserDialogTarget.AccountType = userType;
		this.dirUserDialogVisible = true;
		this.setDirUserDialogDomains(userType);
	}

	private setDirUserDialogDomains(userType: AccountType): void {
		switch (userType) {
			case AccountType.ActiveDirectory:
				this.dirUserDialogDomains = this.adSettings.DomainNames.ToArray();
				break;
			case AccountType.GoogleWorkspace:
				this.dirUserDialogDomains = this.gwSettings.DomainNames.ToArray();
				break;
			default:
				console.assert(false, "Invalid AccountType");
				break;
		}
	}

	public editUser(user: UserDto): void {
		if (user.AccountType == AccountType.Native || user.AccountType == AccountType.Saml) {
			this.userDialogCreate = false;
			this.userDialogTarget = user;
			this.userDialogVisible = true;
		}
		else if (user.AccountType == AccountType.ActiveDirectory) {
			this.identityService.testAdmin(AccountType.ActiveDirectory).subscribe(
				(result: LoginResultDto) => {
					if (result.Code === LoginResultCode.Succeeded) {
						this.editDirectoryUser(user);
					}
					else {
						this.dirUserDialogCreate = false;
						this.dirUserDialogTarget = user;

						this.adAdminDialogDomains = this.usersService.adSettings.DomainNames.ToArray();
						this.adAdminDialogVisible = true;
					}
				},
				(error) => {
					this.messageService.displayErrorMessage("Could not verify Active Directory admin credentials");
				}
			);
		}
		else if (user.AccountType == AccountType.GoogleWorkspace) {
			this.identityService.testAdmin(AccountType.GoogleWorkspace).subscribe(
				(result: LoginResultDto) => {
					if (result.Code == LoginResultCode.Succeeded) {
						this.editDirectoryUser(user);
					}
					else {
						this.dirUserDialogCreate = false;
						this.dirUserDialogTarget = user;

						this.gwAdminDialogVisible = true;
					}
				},
				(error) => {
					this.messageService.displayErrorMessage("Could not verify Google admin credentials");
				}
			);
		}
	}

	private editDirectoryUser(user: UserDto): void {
		this.dirUserDialogType = user.AccountType;
		this.dirUserDialogCreate = false;
		this.dirUserDialogTarget = user;
		this.dirUserDialogVisible = true;
	}

	public editDirectoryUserByGuid(userGuid: string): void {
		if (this.hasEntries) {
			let user: UserDto = this.usersService.users.ToArray().find((user: UserDto) => user.Guid === userGuid);
			if (user) {
				this.dirUserDialogType = user.AccountType;
				this.dirUserDialogCreate = false;
				this.dirUserDialogTarget = user;
				this.dirUserDialogVisible = true;
			}
		}
		else {
			this.contextUserGuid = userGuid;
		}
	}

	public deleteUser(user: UserDto): void {
		if (this.deleteIsSafe(user)) {
			this.userToDelete = user;
			this.confirmDeleteDialogVisible = true;
		}
		else {
			this.messageService.displayErrorMessage("Cannot remove last administrator");
		}
	}

	private deleteIsSafe(user: UserDto): boolean {
		// Do not allow the last active native admin user to be deleted
		if (this.isActiveNativeAdmin(user)) {
			let activeNativeAdmins: UserDto[] = this.activeNativeAdmins;
			return (activeNativeAdmins.length > 1);
		}
		else {
			return true;
		}
	}

	public onAdminLoginFailed(): void {
		this.messageService.displayErrorMessage("Login failed");
	}

	private get activeNativeAdmins(): UserDto[] {
		let activeNativeAdmins: UserDto[] = [];
		if (this.entries) {
			this.entries.forEach((e) => {
				let user: UserDto = e.tag;
				if (this.isActiveNativeAdmin(user)) {
					activeNativeAdmins.push(user);
				}
			});
		}
		return activeNativeAdmins;
	}

	private isActiveNativeAdmin(user: UserDto): boolean {
		return (user.IsAdmin && user.IsActive && user.AccountType == AccountType.Native);
	}

	private getAllUsers(): void {
		this.usersService.getAllUsers().subscribe(
			(result: List<UserDto>) => {
				this.rebuildEntries(result);
				if (this.contextUserGuid) {
					this.editDirectoryUserByGuid(this.contextUserGuid);
				}
				this.resetContextState();
			},
			(error) => {
				this.messageService.displayErrorMessage("Could not retrieve user information");
				this.rebuildEntries(new List<UserDto>());
				this.resetContextState();
			});
	}

	private rebuildEntries(users: List<UserDto>): void {
		this.entries = [];

		// Initialize entries
		users.ForEach((user: UserDto) => {
			this.addEntry(user);
		}, this);
	}

	private addEntry(user: UserDto): void {
		if (!this.entries.some((entry) => entry.tag.Guid === user.Guid)) {
			let entry = {
				username: user.Username,
				firstname: user.FirstName,
				lastname: user.LastName,
				email: user.Email,
				class: UserClass[user.UserClass],
				type: getAccountTypeLabel(user.AccountType),
				active: (user.IsActive ? 'Yes' : 'No'),
				tag: user
			};

			this.entries.push(entry);
		}
	}

	private updateEntry(user: UserDto): void {
		let found = this.entries.some((entry) => {
			if (entry.tag == user) {
				entry.username = user.Username;
				entry.firstname = user.FirstName;
				entry.lastname = user.LastName;
				entry.email = user.Email;
				entry.class = UserClass[user.UserClass];
				entry.type = getAccountTypeLabel(user.AccountType),
				entry.active = (user.IsActive ? 'Yes' : 'No');
				return true;
			}
			else {
				return false;
			}
		});

		if (!found)
			console.error("Did not find user entry to update");
	}

	private removeEntry(user: UserDto): void {
		for (let i = 0; i < this.entries.length; ++i) {
			if (this.entries[i].tag == user) {
				this.entries.splice(i, 1);
				break;
			}
		}
	}

	private triggerChangeDetection(): void {
		this.entries = [...this.entries];
	}

	public onUserDialogOkClicked(user: UserDto): void {
		if (this.userDialogCreate) {
			// If new user is being created, append entry for it
			this.addEntry(user);
		}
		else {
			// If existing user is being updated, refresh the existing
			// entry with the user's updated properties
			this.updateEntry(user);
		}

		// Force redisplay of modified entries
		this.triggerChangeDetection();

		this.resetUserDialogState();
	}

	public onUserDialogCancelClicked(): void {
		this.resetUserDialogState();
	}

	public onDirUserDialogOkClicked(user: UserDto): void {
		if (this.dirUserDialogCreate) {
			// If new user is being created, append entry for it
			this.addEntry(user);
		}
		else {
			// If existing user is being updated, refresh the existing
			// entry with the user's updated properties
			this.updateEntry(user);
		}

		// Force redisplay of modified entries
		this.triggerChangeDetection();

		this.resetDirUserDialogState();
	}

	public onDirUserDialogCancelClicked(): void {
		this.resetDirUserDialogState();
	}

	public onDeleteConfirmDialogOkClicked(): void {
		let userToDelete = this.userToDelete;
		this.userToDelete = null;
		this.confirmDeleteDialogVisible = false;

		if (userToDelete) {
			this.usersService.deleteUser(userToDelete.Guid).subscribe(
				(result: boolean) => {
					// Remove deleted entry
					this.removeEntry(userToDelete);

					// Force redisplay of modified entries
					this.triggerChangeDetection();
				},
				(error) => {
					this.messageService.displayErrorMessage("Deleting user failed");
				});
		}
	}

	public onDeleteConfirmDialogCancelClicked(): void {
		this.userToDelete = null;
		this.confirmDeleteDialogVisible = false;
	}

}
